'use client';

import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import type { Locale } from '@/lib/i18n';

interface LocaleCtx {
  locale: Locale;
  setLocale: (l: Locale) => void;
  dir: 'ltr' | 'rtl';
}

const Ctx = createContext<LocaleCtx | null>(null);

const STORAGE_KEY = 'sophia_landing_locale';

export function LocaleProvider({ children }: { children: ReactNode }) {
  const [locale, setLocaleState] = useState<Locale>('en');

  // Hydrate locale: ?lang=ar, stored choice, browser language (ar), else en.
  useEffect(() => {
    const url = new URL(window.location.href);
    const qp = url.searchParams.get('lang');
    const stored = window.localStorage.getItem(STORAGE_KEY) as Locale | null;
    const browser = navigator.language.toLowerCase().startsWith('ar') ? 'ar' : 'en';
    const initial: Locale =
      qp === 'ar' || qp === 'en' ? (qp as Locale) : stored ?? (browser as Locale);
    setLocaleState(initial);
  }, []);

  // Sync dir + lang on <html> whenever locale changes
  useEffect(() => {
    const html = document.documentElement;
    html.setAttribute('lang', locale);
    html.setAttribute('dir', locale === 'ar' ? 'rtl' : 'ltr');
  }, [locale]);

  function setLocale(next: Locale) {
    setLocaleState(next);
    try {
      window.localStorage.setItem(STORAGE_KEY, next);
    } catch {
      /* private mode */
    }
  }

  return (
    <Ctx.Provider value={{ locale, setLocale, dir: locale === 'ar' ? 'rtl' : 'ltr' }}>
      {children}
    </Ctx.Provider>
  );
}

export function useLocale(): LocaleCtx {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error('useLocale must be used inside LocaleProvider');
  return ctx;
}
